import { useCallback, useEffect, useState } from 'react';
import { readInstanceIdFromUrl } from './config';
import { EmailGate } from './EmailGate';
import { RoomMark } from './RoomMark';
import { RoomApiError, roomFetch } from './roomFetch';
import { RoomSkeleton } from './RoomSkeleton';
import { RoomWizard } from './RoomWizard';

/**
 * The public Room shell.
 *
 * Resolves the link, then hands off to the email gate (Phase 4.6) and the wizard (Phase 5). It holds
 * the instance id and the resolved link state and nothing else — the session token lives in
 * roomFetch, and answers live in the wizard.
 *
 * Audience assumption that shapes everything here: the visitor is a client of a law or accounting
 * firm, on an unknown device, who did not choose to be here. Every failure needs a plain-language
 * next step, and nothing may ever ask them to log in — they have no account.
 */

/** Mirrors POST /room/resolve. A dead link deliberately carries no client details (nor a logo). */
type ResolvedLink =
  | { state: 'active'; clientName: string; emailHint: string; needsOtp: boolean; logoUrl: string | null }
  | { state: 'revoked' | 'expired' };

type Phase =
  | { kind: 'loading' }
  | { kind: 'no-link' }
  | { kind: 'not-found' }
  | { kind: 'dead'; state: 'revoked' | 'expired' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; instanceId: string; link: Extract<ResolvedLink, { state: 'active' }> };

export function RoomApp() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  // Lifted here (not inside EmailGate) so the gate stays purely "the gate" and RoomApp owns the
  // one-way transition into the wizard. The room token itself lives in sessionStorage (roomFetch);
  // this is just "has the OTP been passed this session".
  const [verified, setVerified] = useState(false);

  const resolve = useCallback(async () => {
    const instanceId = readInstanceIdFromUrl();
    if (!instanceId) {
      setPhase({ kind: 'no-link' });
      return;
    }

    setPhase({ kind: 'loading' });
    try {
      const link = await roomFetch<ResolvedLink>('room/resolve', { body: { instanceId } });
      if (link.state !== 'active') {
        setPhase({ kind: 'dead', state: link.state });
        return;
      }
      setPhase({ kind: 'ready', instanceId, link });
    } catch (err) {
      if (err instanceof RoomApiError && err.status === 404) {
        setPhase({ kind: 'not-found' });
        return;
      }
      setPhase({ kind: 'error', message: err instanceof Error ? err.message : 'משהו השתבש.' });
    }
  }, []);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  // The firm logo comes from /room/resolve, so it is available on both the login gate and the wizard
  // (both live under the 'ready' phase). Dead/loading/error phases carry no logo — they fall back to
  // the Room's own mark. A logo is public firm branding, not directional, so it is never RTL-mirrored.
  const firmLogoUrl = phase.kind === 'ready' ? phase.link.logoUrl : null;

  return (
    <div className="room-shell">
      <header className="room-header">
        {firmLogoUrl ? (
          <img className="room-firm-logo" src={firmLogoUrl} alt="" data-testid="firm-logo" />
        ) : (
          <RoomMark />
        )}
        <span className="room-header-title">השלמת פרטים</span>
      </header>
      <main className="room-main">
        {phase.kind === 'ready' && verified ? (
          <RoomWizard />
        ) : phase.kind === 'ready' ? (
          <EmailGate instanceId={phase.instanceId} link={phase.link} onVerified={() => setVerified(true)} />
        ) : (
          renderPhase(phase, resolve)
        )}
      </main>
    </div>
  );
}

function renderPhase(phase: Phase, retry: () => void) {
  switch (phase.kind) {
    case 'loading':
      return <RoomSkeleton />;

    case 'no-link':
      // Reached by typing the bare domain, or by a mail client mangling the link. Say what is wrong
      // without implying the client did something wrong.
      return (
        <Notice
          title="הקישור חסר"
          body="נראה שהגעתם לכאן ללא קישור אישי. פתחו את הקישור שקיבלתם במייל, או פנו לאיש הקשר שלכם."
        />
      );

    case 'not-found':
      return (
        <Notice
          title="הקישור אינו תקין"
          body="לא מצאנו את התהליך המבוקש. ייתכן שהקישור הועתק חלקית — נסו לפתוח אותו שוב מהמייל המקורי."
        />
      );

    case 'dead':
      // Revoked and expired are told apart because the remedy differs: an expired link can be
      // renewed, a revoked one was a deliberate act. Both point at the same person.
      return phase.state === 'expired' ? (
        <Notice title="תוקף הקישור פג" body="הקישור אינו פעיל עוד. בקשו קישור חדש מאיש הקשר שלכם." />
      ) : (
        <Notice title="הקישור בוטל" body="הגישה לתהליך זה בוטלה. פנו לאיש הקשר שלכם לקבלת פרטים." />
      );

    case 'error':
      return (
        <Notice title="משהו השתבש" body={phase.message} alert>
          <button type="button" className="btn-primary" onClick={retry}>
            נסו שוב
          </button>
        </Notice>
      );

    case 'ready':
      // Handled directly in the component body (EmailGate → RoomWizard); renderPhase is only called
      // for the non-ready phases. This arm exists to keep the switch exhaustive.
      return null;
  }
}

function Notice({ title, body, children, alert = false }: { title: string; body: string; children?: React.ReactNode; alert?: boolean }) {
  return (
    <section className="room-card">
      <h1 className="room-card-title">{title}</h1>
      {/* role="alert" only on the true error surface — a retry-triggered failure must be announced.
          The dead/not-found/no-link notices are the whole first-paint page, not errors, so they are
          left un-asserted. */}
      <p className="room-card-body" role={alert ? 'alert' : undefined}>
        {body}
      </p>
      {children}
    </section>
  );
}
